import React from "react";
import { Select, SelectOption } from "@showitinc/showit-wc";
import FontSelectInput from "@/components/FontSelectInput";
import StyleSelectInput from "@/components/StyleSelectInput";
import AlignOptions from "@/components/elementproperties/AlignOptions";
import FillTypeProperties from "@/components/elementproperties/FillTypeProperties";
import ColumnOptions from "@/components/elementproperties/ColumnOptions";
import ColorPicker from "@/app2components/base/ColorPicker";
import SimplePaddingControl from "@/app2components/panel/element/simple/SimplePaddingControl";
import SplitMergeWrapper from "@/app2components/base/SplitMergeWrapper";
import DropShadowProperties from "@/components/elementproperties/DropShadowProperties";
import Input from "@/app2components/base/Input";
import CornerStyleProperties from "@/components/CornerStyleProperties";
import CornerStylePropertiesDynamic from "@/components/CornerStylePropertiesDynamic";

/**
 * @typedef {typeof import("@/constants/DesignConstants.mjs").LAYOUTS} LAYOUTS
 * @typedef {import("@/utils/Element/Types.mjs").PropData} PropData
 */

/**
 * Wrapper component properties.
 * @param {Object} props
 * @param {boolean} props.canMerge - Indicates if merging is allowed.
 * @param {React.ReactNode | React.ReactElement} props.children - Child components to render.
 * @param {boolean} props.canToggle - Indicates if toggling is allowed.
 * @param {string} props.propName - The name of the param.
 * @param {LAYOUTS[number]} props.layout - The layout context.
 * @param {boolean} props.sync - Indicates if the param is synced.
 * @param {() => void} props.toggleSync - Function to toggle the synced state.
 * @param {Function} props.removeCustomization - Function to remove customization.
 * @param {boolean} props.onlyShowDesktop - Indicates if only desktop view should be shown.
 *
 * @returns {JSX.Element} - Returns a JSX element that conditionally wraps children with a SplitMergeWrapper.
 */
const Wrapper = ({ canMerge, children, ...rest }) =>
  canMerge === false ? (
    <>{children}</>
  ) : (
    <SplitMergeWrapper {...rest}>{children}</SplitMergeWrapper>
  );

/**
 * DynamicInput component properties.
 *
 * @param {Object} props - Component properties.
 * @param {PropData} props.propData - Data related to the property being edited.
 * @param {LAYOUTS[number]} props.layout - The layout context.
 * @param {boolean} [props.noSplitMerge=false] - Flag to disable split/merge functionality.
 * @param {string} [props.colorShift="left"] - Direction for color picker flyout positioning.
 * @returns {JSX.Element} - Returns a JSX element for dynamic input handling.
 */
const DynamicInput = ({ propData, layout, noSplitMerge = false, shift = "left" }) => {
  const {
    canMerge,
    type,
    value,
    onSIChange,
    options = [],
    name,
    isSynced,
    toggleSync,
    removeCustomization,
    onlyShowDesktop,
    canToggle,
  } = propData;
  // todo: add constants
  // todo: update to use superinput

  return (
    <Wrapper
      canMerge={canMerge}
      canToggle={canToggle}
      propName={name}
      layout={layout}
      sync={isSynced}
      toggleSync={toggleSync}
      removeCustomization={removeCustomization}
      onlyShowDesktop={onlyShowDesktop}
    >
      {(type === "text" || type === "number") && <Input {...propData} />}
      {type === "style" && <StyleSelectInput {...propData} />}
      {type === "align" && <AlignOptions {...propData} />}
      {type === "column" && <ColumnOptions {...propData} onPropChange={onSIChange} />}
      {type === "color" && <ColorPicker {...propData} color={value} shift={shift} />}
      {type === "fill" && <FillTypeProperties {...propData} colorShift={shift} />}
      {type === "shadow" && (
        <DropShadowProperties
          {...propData}
          onPropUpdate={onSIChange}
          propName={name}
          shift={shift}
        />
      )}
      {type === "padding" && <SimplePaddingControl {...propData} value={value} />}
      {type === "font" && <FontSelectInput shift="left" {...propData} onChange={onSIChange} />}
      {type === "borderRadius" && <CornerStylePropertiesDynamic {...propData} />}
      {type === "select" && (
        <Select shift="left" {...propData} onSISelect={onSIChange}>
          {options.map((option) => (
            <SelectOption key={option.value} value={option.value}>
              {option.label}
            </SelectOption>
          ))}
        </Select>
      )}
    </Wrapper>
  );
};

export default DynamicInput;
